import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSSEClient } from '../httpSse.client';
import { AuthLoader } from '../auth/authProvider';
import { API_URL } from '../http.client';
import { getOrgId, getUserId } from '../auth';
export function SSEExample() {
    return (React.createElement(AuthLoader, null,
        React.createElement("div", { className: "mx-auto flex max-w-4xl flex-col gap-6 p-6 text-slate-900" },
            React.createElement("section", { className: "rounded-2xl border border-slate-200 bg-white shadow-sm" },
                React.createElement(SSEContent, null)),
            React.createElement("section", { className: "rounded-2xl border border-slate-200 bg-white shadow-sm" },
                React.createElement(AudioStreamDemo, null)))));
}
function SSEContent() {
    const [lastEvent, setLastEvent] = useState(null);
    useEffect(() => {
        const unsubscribe = getSSEClient('fluid-form').subscribe('example/sse', {
            onMessage: (data) => {
                console.log('SSE update:', data);
                setLastEvent(data);
                if (data?.status === 'done') {
                    unsubscribe();
                }
            },
        });
        return () => {
            unsubscribe();
        };
    }, []);
    return (React.createElement("div", { className: "space-y-4 p-6" },
        React.createElement("div", { className: "flex items-center justify-between" },
            React.createElement("div", null,
                React.createElement("p", { className: "text-base font-semibold" }, "Server-sent updates"),
                React.createElement("p", { className: "text-sm text-slate-500" }, "Listening to /example/sse on the backend.")),
            React.createElement("span", { className: "rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600" }, "Live")),
        React.createElement("div", { className: "rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700" },
            React.createElement("p", null, "Loading streaming updates... Check the console for the complete payload details."),
            lastEvent && (React.createElement("pre", { className: "mt-3 max-h-48 overflow-auto rounded-lg bg-white/80 p-3 text-xs text-slate-800" }, JSON.stringify(lastEvent, null, 2))))));
}
function AudioStreamDemo() {
    const [formKey, setFormKey] = useState('fluid-form');
    const [status, setStatus] = useState('idle');
    const [liveTranscript, setLiveTranscript] = useState('');
    const [submissionPreview, setSubmissionPreview] = useState('');
    const [error, setError] = useState(null);
    const wsRef = useRef(null);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const cleanupMedia = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try {
                recorder.stop();
            }
            catch (recorderError) {
                console.warn('Recorder stop error:', recorderError);
            }
        }
        recorderRef.current = null;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, []);
    const stopStreaming = useCallback(() => {
        cleanupMedia();
        const ws = wsRef.current;
        if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
            try {
                ws.close();
            }
            catch (err) {
                console.warn('Failed to close audio stream socket:', err);
            }
        }
        wsRef.current = null;
        setStatus('idle');
    }, [cleanupMedia]);
    const startStreaming = useCallback(async () => {
        if (!formKey.trim()) {
            setError('Form key is required.');
            return;
        }
        const orgId = getOrgId();
        const userId = getUserId();
        if (orgId === 'external' || userId === 'external') {
            setError('You must be signed in (with an org + user) to start audio streaming.');
            return;
        }
        setError(null);
        setLiveTranscript('');
        setSubmissionPreview('');
        setStatus('connecting');
        const url = new URL(API_URL);
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        url.pathname = '/api/fluid-form/audio-stream';
        url.searchParams.set('formKey', formKey.trim());
        url.searchParams.set('orgId', orgId);
        url.searchParams.set('userId', userId);
        const ws = new WebSocket(url.toString());
        wsRef.current = ws;
        ws.addEventListener('open', async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                setError('MediaDevices API is not available in this browser.');
                stopStreaming();
                return;
            }
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = mediaStream;
                const recorderOptions = {};
                if (typeof MediaRecorder !== 'undefined') {
                    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                        recorderOptions.mimeType = 'audio/webm;codecs=opus';
                    }
                    else if (MediaRecorder.isTypeSupported('audio/webm')) {
                        recorderOptions.mimeType = 'audio/webm';
                    }
                }
                if (typeof MediaRecorder === 'undefined') {
                    setError('MediaRecorder API is not supported in this browser.');
                    stopStreaming();
                    return;
                }
                const recorder = new MediaRecorder(mediaStream, recorderOptions);
                recorderRef.current = recorder;
                recorder.ondataavailable = async (event) => {
                    if (!event.data || !event.data.size)
                        return;
                    const socket = wsRef.current;
                    if (!socket || socket.readyState !== WebSocket.OPEN)
                        return;
                    try {
                        const chunk = await event.data.arrayBuffer();
                        socket.send(chunk);
                    }
                    catch (chunkError) {
                        // @TODO: FLU-193 Handle this.
                        console.error('Unable to send audio chunk:', chunkError);
                    }
                };
                recorder.start(600);
                setStatus('recording');
            }
            catch (micError) {
                console.error('Microphone error:', micError);
                setError('Unable to access the microphone. Please allow permissions and try again.');
                stopStreaming();
            }
        });
        ws.addEventListener('message', (event) => {
            try {
                const payload = JSON.parse(event.data);
                if (payload.type === 'partial' && typeof payload.text === 'string') {
                    setLiveTranscript(payload.text);
                    if (payload.submission) {
                        setSubmissionPreview(JSON.stringify(payload.submission, null, 2));
                    }
                }
                else if (payload.type === 'submission' && payload.submission) {
                    if (typeof payload.text === 'string') {
                        setLiveTranscript(payload.text);
                    }
                    setSubmissionPreview(JSON.stringify(payload.submission, null, 2));
                }
                else if (payload.type === 'error' && payload.message) {
                    setError(payload.message);
                }
            }
            catch (parseError) {
                console.warn('Invalid audio stream payload:', event.data);
            }
        });
        ws.addEventListener('error', () => {
            setError('WebSocket connection error.');
            stopStreaming();
        });
        ws.addEventListener('close', () => {
            cleanupMedia();
            if (wsRef.current === ws) {
                wsRef.current = null;
            }
            setStatus('idle');
        });
    }, [cleanupMedia, formKey, stopStreaming]);
    const handleToggle = useCallback(() => {
        if (status === 'idle') {
            startStreaming();
        }
        else {
            stopStreaming();
        }
    }, [startStreaming, status, stopStreaming]);
    useEffect(() => {
        return () => {
            stopStreaming();
        };
    }, [stopStreaming]);
    const buttonLabel = status === 'recording' ? 'Stop streaming' : status === 'connecting' ? 'Connecting…' : 'Start streaming';
    return (React.createElement("div", { className: "space-y-4 p-6" },
        React.createElement("div", null,
            React.createElement("p", { className: "text-base font-semibold" }, "Audio streaming over WebSocket"),
            React.createElement("p", { className: "text-sm text-slate-500" },
                "Captures microphone audio, streams it to ",
                React.createElement("code", { className: "font-mono text-xs" }, "/api/fluid-form/audio-stream"),
                ", and displays partial transcripts from the OpenAI streaming API.")),
        React.createElement("div", { className: "grid gap-3 sm:grid-cols-1" },
            React.createElement("label", { className: "text-sm font-medium text-slate-700" },
                "Form key",
                React.createElement("input", { className: "mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100", placeholder: "fluid-form", value: formKey, onChange: (event) => setFormKey(event.target.value) }))),
        React.createElement("div", { className: "flex flex-wrap items-center gap-3" },
            React.createElement("button", { type: "button", onClick: handleToggle, className: `rounded-full px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${status === 'recording'
                    ? 'bg-rose-500 hover:bg-rose-600'
                    : 'bg-indigo-600 hover:bg-indigo-700'}`, disabled: status === 'idle' && !formKey.trim() }, buttonLabel),
            React.createElement("span", { className: "text-sm text-slate-500" },
                "Status:",
                ' ',
                React.createElement("span", { className: "font-semibold text-slate-900 capitalize" }, status))),
        error && (React.createElement("p", { className: "rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600" }, error)),
        React.createElement("div", { className: "rounded-xl border border-slate-100 bg-slate-50 p-4" },
            React.createElement("p", { className: "text-xs font-semibold uppercase tracking-wide text-slate-500" }, "Live transcript"),
            React.createElement("p", { className: "mt-2 min-h-[3rem] text-sm text-slate-900" }, liveTranscript || 'Speak to stream audio…')),
        submissionPreview && (React.createElement("div", { className: "rounded-xl border border-slate-100 bg-slate-50 p-4" },
            React.createElement("p", { className: "text-xs font-semibold uppercase tracking-wide text-slate-500" }, "Latest submission payload"),
            React.createElement("pre", { className: "mt-2 max-h-64 overflow-auto rounded-lg bg-white/80 p-3 text-xs text-slate-800" }, submissionPreview)))));
}
